import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HiMenuAlt3, 
  HiX, 
  HiHome, 
  HiUser, 
  HiCog, 
  HiCode, 
  HiFolder, 
  HiChat, 
  HiMail 
} from 'react-icons/hi';
import ThemeToggle from './ThemeToggle';
import './Header.css';

const navLinks = [
  { id: 'hero', label: 'Home', icon: HiHome },
  { id: 'about', label: 'About', icon: HiUser },
  { id: 'services', label: 'Services', icon: HiCog },
  { id: 'skills', label: 'Skills', icon: HiCode },
  { id: 'projects', label: 'Projects', icon: HiFolder },
  { id: 'testimonials', label: 'Testimonials', icon: HiChat },
  { id: 'contact', label: 'Contact', icon: HiMail },
];

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  // Handle scroll effect and active section detection
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Detect active section
      const sections = navLinks.map(link => document.getElementById(link.id));
      const scrollPosition = window.scrollY + 100; // Offset for header

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;
          
          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            setActiveSection(navLinks[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [mobileOpen]);

  // Handle escape key to close mobile menu
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && mobileOpen) {
        setMobileOpen(false);
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [mobileOpen]);

  const scrollTo = (id) => {
    // Close mobile menu
    setMobileOpen(false);
    
    // Small delay to ensure menu closes before scrolling
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // Update active section
        setActiveSection(id);
      }
    }, 150);
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <motion.header
      className={`header ${scrolled ? 'header--scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="header__inner">
        <motion.a
          href="#hero"
          className="header__logo"
          onClick={(e) => { 
            e.preventDefault(); 
            scrollTo('hero');
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          Shyam Ranjan
        </motion.a>

        {/* Desktop Navigation - Integrated List */}
        <nav className="header__nav">
          {navLinks.map((link) => (
            <motion.button
              key={link.id}
              className={`header__link ${activeSection === link.id ? 'active' : ''}`}
              onClick={() => scrollTo(link.id)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              {link.label}
            </motion.button>
          ))}
          <ThemeToggle />
        </nav>

        {/* Mobile Header Actions - Theme Toggle and Hamburger */}
        <div className="header__actions">
          <ThemeToggle />
          <motion.button
            className="header__burger"
            onClick={toggleMobileMenu}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            whileTap={{ scale: 0.95 }}
          >
            {mobileOpen ? <HiX className="header__burger-icon" /> : <HiMenuAlt3 className="header__burger-icon" />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu - Full Screen with Integrated List */}
      <AnimatePresence mode="wait">
        {mobileOpen && (
          <motion.div
            className="header__mobile"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {/* Mobile Navigation List with Icons */}
            <div className="header__mobile-nav">
              {navLinks.map((link, i) => {
                const Icon = link.icon;
                return (
                  <motion.button
                    key={link.id}
                    className={`header__mobile-link ${activeSection === link.id ? 'active' : ''}`}
                    onClick={() => scrollTo(link.id)}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ delay: i * 0.08 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Icon className="header__mobile-link-icon" />
                    {link.label}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

export default Header;